/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";
import router from "next/router";
import queryString from "query-string";

import { BasePlateSnapshot, SupportedTheme } from "@vienna/common";

import { API } from "src/classes/api";
import { Plate } from "src/classes/plate/Plate";
import { ClipboardUtils } from "src/classes/utilities/ClipboardUtils";
import { EnvUtils } from "src/classes/utilities/EnvUtils";
import { Toaster } from "src/classes/utilities/Toaster";
import { getCDNDomain } from "src/lib/utils/images";

export class BasePlate<T extends BasePlateSnapshot = BasePlateSnapshot> extends Observable<T> {
  constructor(initialState: T) {
    super(initialState);
  }

  /* -------------------------------------------------- */
  /*                        Methods                     */
  /* -------------------------------------------------- */

  public like = (): void => {
    API.plates.like(this.snapshot.id);
    this.setState({ isLiked: !this.snapshot.isLiked } as Partial<T>);
  };

  public async changePrivacy(): Promise<void> {
    try {
      await API.plates.update(this.snapshot.id, { isPublic: !this.snapshot.isPublic });
      this.setState({ isPublic: !this.snapshot.isPublic } as Partial<T>);
      Toaster.success(`Updated: ${this.snapshot.title}`, this.internalLink);
    } catch (e) {
      Toaster.error("Error updating plate.");
    }
  }

  public delete = async (): Promise<void> => {
    await API.plates.delete(this.snapshot.id, this.snapshot.title);
  };

  public connect = (toPlate: BasePlate | Plate): void => {
    API.plates.connect(this.snapshot.id, toPlate);
  };

  public getThumbnailUrl({ size, theme }: { size: "big" | "small"; theme: SupportedTheme }): string {
    const cdnDomain = getCDNDomain();
    if (this.snapshot.screenshotTime) {
      return size === "big"
        ? `${cdnDomain}/${this.snapshot.id}-${theme}-big.png?updated=${this.snapshot.screenshotTime}`
        : `${cdnDomain}/${this.snapshot.id}-${theme}.png?updated=${this.snapshot.screenshotTime}`;
    } else {
      return size === "big"
        ? `${cdnDomain}/VIENNA-DEFAULT-${theme}-big.png`
        : `${cdnDomain}/VIENNA-DEFAULT-${theme}.png`;
    }
  }

  public open(): void {
    router.replace(this.internalLink, undefined, {
      shallow: true,
    });
  }

  public openInDrilledView(): void {
    router.replace(this.internalDrilledViewLink, undefined, {
      shallow: true,
    });
  }

  public copyLink = (): void => {
    ClipboardUtils.copyTextToClipboard(this.externalLink);
  };

  public openInNewTab = (): void => {
    window.open(this.externalLink, "_blank", "noopener noreferrer");
  };

  /* -------------------------------------------------- */
  /*                       Utils                        */
  /* -------------------------------------------------- */
  public get internalLink(): string {
    return `/plate/${encodeURIComponent(this.snapshot.owner.username)}/${encodeURIComponent(this.snapshot.title)}`;
  }

  private get internalDrilledViewLink(): string {
    return `${router.asPath}?${this.plateQueryParam}`;
  }

  private get plateQueryParam(): string {
    return queryString.stringify({ pageId: this.snapshot.id });
  }

  public get externalLink(): string {
    const tlDomain = EnvUtils.getTopLevelDomain();
    const url = `${tlDomain}${this.internalLink}`;
    return url;
  }
}
